import type { SummonedUnit, SummonType } from "@/types/summon.types"
import type { Enemy } from "@/types/enemy.types"
import type { Position, LaserBeamEffect } from "@/types/game.types"
import { summonConfigs, TURRET_NAMES } from "@/types/summon.types"
import { generateId } from "@/utils/id-generator"
import { distanceSquared } from "@/utils/collision"
import { moveTowards, calculateAngle } from "@/utils/pathfinding"
import { TILE_SIZE } from "@/data/game-settings"
import { getEmptyShrineBuffs } from "./shrine-system"
import { gameNow } from "@/utils/game-clock"

export interface SummonStats {
  damage: number
  health: number
  speed: number
  range: number
  level: number
}

// Create a summoned wolf at position
export const createSummonedWolf = (
  position: Position,
  ownerId: string,
  stats: SummonStats,
  index: number = 0,
): SummonedUnit => {
  const config = summonConfigs["wolf"]
  // Offset wolves slightly from each other
  const offsetAngle = (index * Math.PI * 2) / 2 + Math.PI / 4
  const offsetDistance = 30
  const offsetPosition: Position = {
    x: position.x + Math.cos(offsetAngle) * offsetDistance,
    y: position.y + Math.sin(offsetAngle) * offsetDistance,
  }

  return {
    id: generateId("wolf"),
    type: "wolf",
    ownerId,
    position: offsetPosition,
    targetPosition: null,
    health: stats.health,
    maxHealth: stats.health,
    damage: { min: Math.floor(stats.damage * 0.8), max: stats.damage },
    damageType: config.damageType,
    armorType: config.armorType,
    armor: config.armor,
    attackRange: config.attackRange,
    attackSpeed: config.attackSpeed,
    attackCooldown: 0,
    speed: stats.speed,
    rotation: 0,
    isMoving: false,
    isAttacking: false,
    target: null,
    color: config.color,
    size: config.size,
    isTower: false,
    range: 0,
    projectileSpeed: 0,
    spawnTime: gameNow(),
    level: stats.level,
    // Shrine buffs (initialized to empty)
    shrineBuffs: getEmptyShrineBuffs(),
  }
}

// Create a summoned fox at position
export const createSummonedFox = (
  position: Position,
  ownerId: string,
  stats: SummonStats,
  index: number = 0,
): SummonedUnit => {
  const config = summonConfigs["fox"]
  // Offset from spawn position
  const offsetAngle = (index * Math.PI * 2) / 2 + Math.PI / 4
  const offsetDistance = 25
  const offsetPosition: Position = {
    x: position.x + Math.cos(offsetAngle) * offsetDistance,
    y: position.y + Math.sin(offsetAngle) * offsetDistance,
  }

  return {
    id: generateId("fox"),
    type: "fox",
    ownerId,
    position: offsetPosition,
    targetPosition: null,
    health: stats.health,
    maxHealth: stats.health,
    damage: { min: Math.floor(stats.damage * 0.7), max: stats.damage }, // Fox does less damage
    damageType: config.damageType,
    armorType: config.armorType,
    armor: config.armor,
    attackRange: config.attackRange,
    attackSpeed: config.attackSpeed,
    attackCooldown: 0,
    speed: config.speed, // Use config speed (faster)
    rotation: 0,
    isMoving: false,
    isAttacking: false,
    target: null,
    color: config.color,
    size: config.size,
    isTower: false,
    range: 0,
    projectileSpeed: 0,
    spawnTime: gameNow(),
    level: stats.level,
    // Shrine buffs (initialized to empty)
    shrineBuffs: getEmptyShrineBuffs(),
  }
}

// Create a summoned boar at position
export const createSummonedBoar = (
  position: Position,
  ownerId: string,
  stats: SummonStats,
  index: number = 0,
): SummonedUnit => {
  const config = summonConfigs["boar"]
  // Offset from spawn position (opposite side from fox)
  const offsetAngle = (index * Math.PI * 2) / 2 + Math.PI / 4 + Math.PI
  const offsetDistance = 30
  const offsetPosition: Position = {
    x: position.x + Math.cos(offsetAngle) * offsetDistance,
    y: position.y + Math.sin(offsetAngle) * offsetDistance,
  }

  return {
    id: generateId("boar"),
    type: "boar",
    ownerId,
    position: offsetPosition,
    targetPosition: null,
    health: stats.health,
    maxHealth: stats.health,
    damage: { min: Math.floor(stats.damage * 0.85), max: Math.floor(stats.damage * 1.2) }, // Boar does more damage
    damageType: config.damageType,
    armorType: config.armorType,
    armor: config.armor,
    attackRange: config.attackRange,
    attackSpeed: config.attackSpeed,
    attackCooldown: 0,
    speed: config.speed, // Use config speed (slower)
    rotation: 0,
    isMoving: false,
    isAttacking: false,
    target: null,
    color: config.color,
    size: config.size,
    isTower: false,
    range: 0,
    projectileSpeed: 0,
    spawnTime: gameNow(),
    level: stats.level,
    // Shrine buffs (initialized to empty)
    shrineBuffs: getEmptyShrineBuffs(),
  }
}

// Create a siege tower at position
export const createSummonedTower = (
  position: Position,
  ownerId: string,
  stats: SummonStats,
): SummonedUnit => {
  const config = summonConfigs["siege-tower"]
  // Pick a random turret name
  const randomName = TURRET_NAMES[Math.floor(Math.random() * TURRET_NAMES.length)]

  return {
    id: generateId("siege-tower"),
    type: "siege-tower",
    name: randomName,
    ownerId,
    position: { ...position },
    targetPosition: null,
    health: stats.health,
    maxHealth: stats.health,
    damage: { min: Math.floor(stats.damage * 0.8), max: stats.damage },
    damageType: config.damageType,
    armorType: config.armorType,
    armor: config.armor,
    attackRange: stats.range * TILE_SIZE,
    attackSpeed: config.attackSpeed,
    attackCooldown: 0,
    speed: 0, // Towers don't move
    rotation: 0,
    isMoving: false,
    isAttacking: false,
    target: null,
    color: config.color,
    size: config.size,
    isTower: true,
    range: stats.range * TILE_SIZE,
    projectileSpeed: config.projectileSpeed,
    spawnTime: gameNow(),
    level: stats.level,
    // Shrine buffs (initialized to empty)
    shrineBuffs: getEmptyShrineBuffs(),
  }
}

// Calculate damage for a summoned unit (with shrine damage bonus applied)
export const calculateSummonDamage = (summon: SummonedUnit): number => {
  // Apply shrine damage bonus
  const mult = 1 + summon.shrineBuffs.damageBonus
  const min = Math.floor(summon.damage.min * mult)
  const max = Math.floor(summon.damage.max * mult)
  return Math.floor(min + Math.random() * (max - min + 1))
}

// Find the nearest enemy for a summoned unit to target
export const findSummonTarget = (
  summon: SummonedUnit,
  enemies: Enemy[],
): Enemy | null => {
  let nearestEnemy: Enemy | null = null
  let minDistanceSquared = Number.POSITIVE_INFINITY
  const attackRangeSquared = summon.attackRange * summon.attackRange

  for (const enemy of enemies) {
    if (enemy.health <= 0 || enemy.reachedEnd) continue

    const distSq = distanceSquared(summon.position, enemy.position)
    if (distSq <= attackRangeSquared && distSq < minDistanceSquared) {
      minDistanceSquared = distSq
      nearestEnemy = enemy
    }
  }

  return nearestEnemy
}

// Set wolf destination (for player control)
export const setSummonDestination = (
  summon: SummonedUnit,
  destination: Position,
): SummonedUnit => {
  if (summon.isTower) return summon // Towers can't move

  return {
    ...summon,
    targetPosition: { ...destination },
    isMoving: true,
  }
}

// Update a wolf's movement and targeting
export const updateSummonedWolf = (
  wolf: SummonedUnit,
  enemies: Enemy[],
  delta: number,
): { unit: SummonedUnit; hitEnemyIds: string[] } => {
  const updated = { ...wolf }
  const hitEnemyIds: string[] = []

  // Update attack cooldown
  if (updated.attackCooldown > 0) {
    updated.attackCooldown -= delta
  }

  // Check if wolf has a manual move order
  if (updated.targetPosition) {
    const distSq = distanceSquared(updated.position, updated.targetPosition)
    if (distSq < 25) {
      // Reached destination
      updated.targetPosition = null
      updated.isMoving = false
    } else {
      // Move towards destination (apply shrine speed bonus)
      const effectiveSpeed = updated.speed * (1 + updated.shrineBuffs.movementSpeedBonus)
      updated.position = moveTowards(
        updated.position,
        updated.targetPosition,
        effectiveSpeed * delta,
      )
      updated.isMoving = true
      updated.rotation = calculateAngle(updated.position, updated.targetPosition)

      // Can still attack while moving if enemy is in range
      const nearestEnemy = findSummonTarget(updated, enemies)
      if (nearestEnemy) {
        updated.isAttacking = true
        updated.target = nearestEnemy.id
        updated.rotation = calculateAngle(updated.position, nearestEnemy.position)

        // Attack if cooldown ready
        if (updated.attackCooldown <= 0) {
          hitEnemyIds.push(nearestEnemy.id)
          updated.attackCooldown = updated.attackSpeed
        }
      } else {
        updated.isAttacking = false
        updated.target = null
      }

      return { unit: updated, hitEnemyIds }
    }
  }

  // No move order - find enemy to attack
  const nearestEnemy = findSummonTarget(updated, enemies)

  if (nearestEnemy) {
    // Move towards enemy if not in melee range
    const distSq = distanceSquared(updated.position, nearestEnemy.position)
    const meleeRange = 40 // Close range for melee attack
    const meleeRangeSq = meleeRange * meleeRange

    if (distSq > meleeRangeSq) {
      // Move closer (apply shrine speed bonus)
      const effectiveSpeed = updated.speed * (1 + updated.shrineBuffs.movementSpeedBonus)
      updated.position = moveTowards(
        updated.position,
        nearestEnemy.position,
        effectiveSpeed * delta,
      )
      updated.isMoving = true
    } else {
      updated.isMoving = false
    }

    updated.isAttacking = true
    updated.target = nearestEnemy.id
    updated.rotation = calculateAngle(updated.position, nearestEnemy.position)

    // Attack if cooldown ready and in range
    if (updated.attackCooldown <= 0 && distSq <= meleeRangeSq) {
      hitEnemyIds.push(nearestEnemy.id)
      updated.attackCooldown = updated.attackSpeed
    }
  } else {
    updated.isAttacking = false
    updated.target = null
    updated.isMoving = false
  }

  return { unit: updated, hitEnemyIds }
}

// Laser tick interval in milliseconds
const LASER_TICK_INTERVAL_MS = 100

// Update a siege tower's targeting and laser beam
export const updateSummonedTower = (
  tower: SummonedUnit,
  enemies: Enemy[],
  currentLaser: LaserBeamEffect | null,
): { unit: SummonedUnit; laserEffect: LaserBeamEffect | null } => {
  const updated = { ...tower }

  // Find nearest enemy in range
  const nearestEnemy = findSummonTarget(updated, enemies)

  if (nearestEnemy) {
    updated.isAttacking = true
    updated.target = nearestEnemy.id
    updated.rotation = calculateAngle(updated.position, nearestEnemy.position)

    // Create or update laser effect
    const damage = calculateSummonDamage(updated)
    const now = gameNow()

    if (currentLaser && currentLaser.targetId === nearestEnemy.id) {
      // Update existing laser - keep same effect, update positions
      const laserEffect: LaserBeamEffect = {
        ...currentLaser,
        sourcePosition: { ...updated.position },
        targetPosition: { ...nearestEnemy.position },
        isActive: true,
      }
      return { unit: updated, laserEffect }
    } else {
      // Create new laser for new target
      const laserEffect: LaserBeamEffect = {
        id: generateId("laser"),
        sourceId: updated.id,
        targetId: nearestEnemy.id,
        sourcePosition: { ...updated.position },
        targetPosition: { ...nearestEnemy.position },
        damage,
        tickInterval: LASER_TICK_INTERVAL_MS,
        lastTickTime: now,
        isActive: true,
      }
      return { unit: updated, laserEffect }
    }
  } else {
    updated.isAttacking = false
    updated.target = null
    // No target - deactivate laser
    return { unit: updated, laserEffect: null }
  }
}

// Damage a summoned unit
export const damageSummon = (
  summon: SummonedUnit,
  damage: number,
): SummonedUnit => {
  const now = gameNow()
  return {
    ...summon,
    health: Math.max(0, summon.health - damage),
    // Trigger hurt animation for 300ms (for turrets)
    hurtEndTime: summon.isTower ? now + 300 : summon.hurtEndTime,
  }
}

// Heal a summoned unit to full health
export const healSummon = (summon: SummonedUnit): SummonedUnit => {
  return {
    ...summon,
    health: summon.maxHealth,
  }
}

// Check if a summon is dead
export const isSummonDead = (summon: SummonedUnit): boolean => {
  const health = Number(summon.health)
  // Treat health < 1 as dead to catch floating point issues
  return health < 1 || Number.isNaN(health)
}

// Get all summons of a specific type owned by a hero (excludes dead summons)
export const getSummonsByType = (
  summons: SummonedUnit[],
  ownerId: string,
  type: SummonType,
): SummonedUnit[] => {
  return summons.filter((s) => s.ownerId === ownerId && s.type === type && !isSummonDead(s))
}

// Get all summons owned by a hero (excludes dead summons)
export const getSummonsByOwner = (
  summons: SummonedUnit[],
  ownerId: string,
): SummonedUnit[] => {
  return summons.filter((s) => s.ownerId === ownerId && !isSummonDead(s))
}

/**
 * Get effective max health with shrine health bonus applied
 */
export const getEffectiveSummonMaxHealth = (summon: SummonedUnit): number => {
  return Math.floor(summon.maxHealth * (1 + summon.shrineBuffs.healthBonus))
}

/**
 * Get effective damage with shrine damage bonus applied
 */
export const getEffectiveSummonDamage = (summon: SummonedUnit): number => {
  // Apply damage bonus to max damage, maintaining min/max ratio
  return Math.floor(summon.damage.max * (1 + summon.shrineBuffs.damageBonus))
}
